import { formatCurrencyNoDecimals, formatPercentage } from '@if/core/utils'
import {
  ActionButton,
  Banner,
  BodyText,
  Caption,
  Dash,
  DataField,
  EnumChip,
  MetaValue,
  StatCard,
  StrongValue,
  SubsectionTitle,
  type TranslationKey,
  useCan,
  useCollapse,
} from '@if/mui'
import { access } from '@src/access'
import { Box, ButtonBase, Card, CardContent, Collapse, Divider, Skeleton, Stack } from '@mui/material'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import {
  useCustomBuydownPlanHistoryQuery,
  useExpiredCustomBuydownPlansQuery,
} from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { FinancingCompanyConfigs } from '@src/types/Constants'
import { CustomBuydownHistoryEntry, CustomBuydownPlan, ExpiredCustomBuydownPlan } from '@src/types/CustomBuydown'
import { EPlanStatus } from '@src/types/enumSets'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  diffRevisions,
  formatEasternDate,
  formatEasternDateTime,
  getRevisionAuthor,
  isRatesOnlyChange,
  type PlanChange,
  type PlanField,
} from '../planHistory'

const HISTORY_SKELETON_COUNT = 2

const formatPercent = (value: number) => `${formatPercentage(value * 100)}%`

const useFormatters = () => {
  const { t } = useTranslation()
  const formatCap = (value: number | null) =>
    value === null ? t('customBuydown.history.noMaximum') : formatCurrencyNoDecimals(value)
  const formatField = (field: PlanField, value: number | null) =>
    field === 'maxFinancingAmount' ? formatCap(value) : value === null ? '—' : formatPercent(value)
  return { formatCap, formatField }
}

const FIELD_LABEL_KEYS = {
  contribution: 'customBuydown.history.contribution',
  adminFee: 'customBuydown.history.adminFee',
  maxFinancingAmount: 'customBuydown.maxFinancingAmount',
} as const satisfies Record<PlanField, TranslationKey>

interface RateTile {
  termMonths: number
  promoRate: number | null
  /** The rate this one replaced; omitted on a plan's first version. */
  previousRate?: number | null
}

const formatRate = (rate: number | null) => (rate === null ? '—' : formatPercent(rate))

/** `~~16.34%~~ → 18.34%` on one line, the way Merchants shows a value next to the one it replaced. */
const RateValue = ({ rate }: { rate: RateTile }) =>
  rate.previousRate === undefined ? (
    <>{formatRate(rate.promoRate)}</>
  ) : (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      <Box component="s" sx={{ color: 'text.secondary' }}>
        {formatRate(rate.previousRate)}
      </Box>
      {' → '}
      {formatRate(rate.promoRate)}
    </Box>
  )

/** One tile per term: the rate that applies, beside the rate it replaced when there was one. */
const RateTiles = ({ rates }: { rates: RateTile[] }) => {
  const { t } = useTranslation()

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(64px, 1fr))', gap: 1 }}>
      {rates.map((rate) => (
        <StatCard
          key={rate.termMonths}
          size="xs"
          label={t('customBuydown.history.termShort', { months: rate.termMonths })}
          value={<RateValue rate={rate} />}
        />
      ))}
    </Box>
  )
}

const PlanTerms = ({
  merchantFeeRate,
  borrowerFeeRate,
  maxFinancingAmount,
}: Pick<ExpiredCustomBuydownPlan, 'merchantFeeRate' | 'borrowerFeeRate' | 'maxFinancingAmount'>) => {
  const { t } = useTranslation()
  const { formatCap } = useFormatters()

  return (
    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
      <DataField label={t('customBuydown.history.contribution')} value={formatPercent(merchantFeeRate)} />
      <DataField label={t('customBuydown.history.adminFee')} value={formatPercent(borrowerFeeRate)} />
      <DataField label={t('customBuydown.maxFinancingAmount')} value={formatCap(maxFinancingAmount)} />
    </Stack>
  )
}

const RevisionAuthor = ({ entry }: { entry: CustomBuydownHistoryEntry }) => {
  const { t } = useTranslation()
  const merchant = useMerchant()
  const author = getRevisionAuthor(entry)

  const name =
    author.kind === 'financingCompany'
      ? FinancingCompanyConfigs[merchant.financingCompanyId].name
      : author.kind === 'headOffice'
        ? t('customBuydown.history.headOffice')
        : author.name

  return <Caption>{t('customBuydown.history.by', { author: name })}</Caption>
}

type FieldChange = Extract<PlanChange, { kind: 'field' }>
type RateChange = Extract<PlanChange, { kind: 'rate' }>

const ChangeLine = ({ change }: { change: FieldChange }) => {
  const { t } = useTranslation()
  const { formatField } = useFormatters()

  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Caption sx={{ minWidth: 140 }}>{t(FIELD_LABEL_KEYS[change.field])}</Caption>
      <BodyText sx={{ fontVariantNumeric: 'tabular-nums' }}>
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {formatField(change.field, change.from)}
        </Box>
        {' → '}
        <strong>{formatField(change.field, change.to)}</strong>
      </BodyText>
    </Stack>
  )
}

const RevisionBody = ({
  entry,
  previous,
}: {
  entry: CustomBuydownHistoryEntry
  previous: CustomBuydownHistoryEntry | undefined
}) => {
  const { t } = useTranslation()

  if (!previous) {
    return (
      <Stack spacing={1.5}>
        <Caption>{t('customBuydown.history.created')}</Caption>
        <PlanTerms {...entry} />
        <RateTiles rates={entry.promoTermRates} />
      </Stack>
    )
  }

  const changes = diffRevisions(entry, previous)
  if (changes.length === 0) return <Caption>{t('customBuydown.history.noChange')}</Caption>

  const fieldChanges = changes.filter((change): change is FieldChange => change.kind === 'field')
  const rateChanges = changes.filter((change): change is RateChange => change.kind === 'rate')

  return (
    <Stack spacing={1.5}>
      {isRatesOnlyChange(changes) && <Caption>{t('customBuydown.history.ratesRecalculated')}</Caption>}
      {fieldChanges.length > 0 && (
        <Stack spacing={0.5}>
          {fieldChanges.map((change) => (
            <ChangeLine key={change.field} change={change} />
          ))}
        </Stack>
      )}
      {rateChanges.length > 0 && (
        <RateTiles
          rates={rateChanges.map((change) => ({
            termMonths: change.termMonths,
            promoRate: change.to,
            previousRate: change.from,
          }))}
        />
      )}
    </Stack>
  )
}

/**
 * Every saved revision of one plan, newest first, as a timeline: each entry lists only what it changed
 * from the revision before it. Fetched once the timeline is opened.
 */
const PlanChangeTimeline = ({ merchantId, planKey, open }: { merchantId: string; planKey: string; open: boolean }) => {
  const { t, i18n } = useTranslation()
  const canSeeHistory = useCan(access.customBuydownHistory)
  const {
    data: history,
    isLoading,
    isError,
  } = useCustomBuydownPlanHistoryQuery(merchantId, planKey, canSeeHistory && open)

  if (isError) return <Banner severity="error">{t('customBuydown.history.loadError')}</Banner>

  if (isLoading || !history)
    return (
      <Stack spacing={1}>
        {Array.from({ length: HISTORY_SKELETON_COUNT }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={56} />
        ))}
      </Stack>
    )

  return (
    <Stack component="ol" spacing={2.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {history.map((entry, index) => (
        <Box
          component="li"
          key={entry.id}
          sx={{ position: 'relative', pl: 3, borderLeft: 1, borderColor: 'divider', ml: 0.75 }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: -6,
              top: 4,
              width: 11,
              height: 11,
              borderRadius: '50%',
              bgcolor: index === 0 ? 'primary.main' : 'divider',
            }}
          />
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
              <StrongValue>{formatEasternDateTime(entry.effectiveOn, i18n.language)}</StrongValue>
              <RevisionAuthor entry={entry} />
            </Stack>
            <RevisionBody entry={entry} previous={history[index + 1]} />
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}

const DisclosureHeader = ({
  title,
  subtitle,
  icon,
  triggerProps,
}: {
  title: ReactNode
  subtitle: ReactNode
  icon: ReactNode
  triggerProps: ReturnType<typeof useCollapse>['triggerProps']
}) => (
  <ButtonBase
    {...triggerProps}
    sx={{
      display: 'flex',
      width: '100%',
      justifyContent: 'space-between',
      alignItems: 'center',
      textAlign: 'left',
      gap: 2,
    }}
  >
    <Box>
      <SubsectionTitle>{title}</SubsectionTitle>
      <Caption>{subtitle}</Caption>
    </Box>
    {icon}
  </ButtonBase>
)

/** The change history of the merchant's current plan, collapsed under the builder. */
export const CurrentPlanHistory = ({ merchantId, plan }: { merchantId: string; plan: CustomBuydownPlan }) => {
  const { t } = useTranslation()
  const canSeeHistory = useCan(access.customBuydownHistory)
  const { expanded, triggerProps, regionProps, icon } = useCollapse()

  if (!canSeeHistory) return null

  return (
    <Card>
      <CardContent>
        <DisclosureHeader
          title={t('customBuydown.history.currentPlanTitle')}
          subtitle={`${t('customBuydown.history.currentPlanSubtitle', { planCode: plan.label })} ${t('customBuydown.history.timesInEastern')}`}
          icon={icon}
          triggerProps={triggerProps}
        />
        <Collapse {...regionProps}>
          <Box sx={{ pt: 3 }}>
            <PlanChangeTimeline merchantId={merchantId} planKey={plan.planKey} open={expanded} />
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

const ExpiredPlanRow = ({ merchantId, plan }: { merchantId: string; plan: ExpiredCustomBuydownPlan }) => {
  const { t, i18n } = useTranslation()
  const { expanded, triggerProps, regionProps } = useCollapse()

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
              <StrongValue>{plan.label}</StrongValue>
              <EnumChip set={EPlanStatus} value={EPlanStatus.Expired} size="small" />
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <StrongValue sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('customBuydown.history.period', {
                  from: formatEasternDate(plan.activeFrom),
                  to: formatEasternDate(plan.expiresOn),
                })}
              </StrongValue>
              <Dash />
              <Caption>{t('customBuydown.history.endedOn')}</Caption>
              <MetaValue sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatEasternDateTime(plan.expiresOn, i18n.language)}
              </MetaValue>
            </Stack>
          </Stack>
          <PlanTerms {...plan} />
          <RateTiles rates={plan.promoTermRates} />
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          <ActionButton size="small" startIcon={expanded ? ChevronUp : ChevronDown} {...triggerProps}>
            {expanded ? t('customBuydown.history.hideChanges') : t('customBuydown.history.showChanges')}
          </ActionButton>
          <Collapse {...regionProps}>
            <Box sx={{ pt: 2 }}>
              <PlanChangeTimeline merchantId={merchantId} planKey={plan.planKey} open={expanded} />
            </Box>
          </Collapse>
        </Box>
      </CardContent>
    </Card>
  )
}

/** The merchant's plans that no longer apply, most recently ended first. Hidden while there are none. */
export const ExpiredPlansSection = ({ merchantId }: { merchantId: string }) => {
  const { t } = useTranslation()
  const canSeeHistory = useCan(access.customBuydownHistory)
  const { data: plans } = useExpiredCustomBuydownPlansQuery(merchantId, canSeeHistory)
  const { triggerProps, regionProps, icon } = useCollapse({ defaultExpanded: true })

  if (!canSeeHistory || !plans?.length) return null

  return (
    <Box component="section">
      <Divider sx={{ mb: 3 }} />
      <DisclosureHeader
        title={t('customBuydown.history.expiredPlansTitle')}
        subtitle={`${t('customBuydown.history.expiredPlansCount', { count: plans.length })} ${t('customBuydown.history.timesInEastern')}`}
        icon={icon}
        triggerProps={triggerProps}
      />
      <Collapse {...regionProps}>
        <Stack spacing={2} sx={{ pt: 3 }}>
          {plans.map((plan) => (
            <ExpiredPlanRow key={plan.planKey} merchantId={merchantId} plan={plan} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  )
}
